// src/tattoo/LeftPanelMobileContent.jsx
import React from "react";
import {
  clearAll,
  setPoseWeight,
  downloadUVGuide,
  downloadAllUVGuides,
} from "./state";
import AuthButton from "./components/AuthButton.jsx";
import MobileMorphPanel from "./MobileMorphPanel.jsx";
import FlashLibrary from "../flash/FlashLibrary";
import SkinToneSection from "./leftPanel/sections/SkinToneSection";
import LeadFormSection from "./leftPanel/sections/LeadFormSection";
import ArtistWizardSection from "./leftPanel/sections/ArtistWizardSection";
import JoinWizardSection from "./leftPanel/sections/JoinWizardSection";
import ArtistDirectorySection from "./leftPanel/sections/ArtistDirectorySection";
import FlashSection from "./leftPanel/sections/FlashSection";
import AppearanceSection from "./leftPanel/sections/AppearanceSection";
import LayersSection from "./leftPanel/sections/LayersSection";
import ProjectNameField from "./leftPanel/ProjectNameField";

export default function LeftPanelMobileContent({
  lp,
  panelMode = "layers",
  setPanelMode = () => {},
  onExportPreview,
  onExportPreviewVideo,
  onJumpToArtistDirectory = () => {},
  renderedImageUrls = [],
  placementUrl = null,
  onRefreshPreview = () => {},
}) {
  const {
    snap, layerFileRef, decalFileRef, onUploadLayer, onUploadDecal,
    opts, layersPartKey, uiLayers, hasSomethingToShare,
    hairIsNone,
    showTemplates, setShowTemplates,
    quoteOpen, setQuoteOpen,
    showJoin, setShowJoin,
    preparedVideo, setPreparedVideo,
    videoBusy, setVideoBusy,
    videoPreset, setVideoPreset,
    skinOpen, setSkinOpen,
    appearanceOpen, setAppearanceOpen,
    topMode, topPreset, topCustom,
    bottomMode, bottomPreset, bottomCustom,
    hairMode, hairPreset, hairCustom,
    setTop, setBottom, setHair,
    showArtistWizard, setShowArtistWizard,
    showArtistUpgrade, setShowArtistUpgrade,
    openArtistWizardGated, artistTrialStarted, artistTrialDaysLeft,
    shopProfile, shopProfileLoading, hasShopProfile, shopNeedsReactivation,
    onSubmitArtistProfile,
  } = lp;

  if (panelMode === "morphs") {
    return (
      <div className="tt-morph-page">
        <div className="tt-morph-subheader">
          <button type="button" className="tt-btn" onClick={() => setPanelMode("layers")}>
            ← Back
          </button>
          <div className="tt-morph-title">Body Morphs</div>
        </div>
        <MobileMorphPanel />
      </div>
    );
  }

  if (panelMode === "flash") {
    return (
      <div className="tt-flash-page">
        <div className="tt-flash-subheader">
          <button type="button" className="tt-btn" onClick={() => setPanelMode("layers")}>
            ← Back
          </button>
          <div className="tt-flash-title">Flash</div>
        </div>
        <FlashLibrary onSelectFlash={(item) => console.log(item)} />
      </div>
    );
  }

  function poseThumbSrc(label) {
    const t = String(label || "")
      .trim()
      .toLowerCase();

    if (t === "neutral") return "/poses/Neutral.svg";
    if (t === "Right Arm" || t === "Right Arm") return "/poses/A-Pose.svg";

    // fallback
    return "/poses/Neutral.svg";
  }
  return (
    <>
      {/* Header (NOT sticky here — the sheet owns stickiness) */}
      <div
        style={{
          position: "relative",
          zIndex: 20,
          paddingBottom: 8,
          marginBottom: 8,
          background: "transparent",
          borderBottom: "none",
        }}
      >
        <div
          className="tt-row"
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div className="tt-row" style={{ gap: 8, alignItems: "center" }}>
            <AuthButton className="tt-auth-btn tt-grow" />
          </div>

          <button
            type="button"
            className="tt-btn tt-find-artist"
            onClick={onJumpToArtistDirectory}
          >
            Find an artist ↓
          </button>

          {/* No ≡ button here; sheet owns open/close */}
        </div>

        <div style={{ marginTop: 10 }}>
          <ProjectNameField />

          <LayersSection
            layerFileRef={layerFileRef} decalFileRef={decalFileRef}
            onUploadLayer={onUploadLayer} onUploadDecal={onUploadDecal}
            onOpenArtistWizard={openArtistWizardGated}
            setShowArtistWizard={setShowArtistWizard}
            shopProfileLoading={shopProfileLoading} hasShopProfile={hasShopProfile}
            shopNeedsReactivation={shopNeedsReactivation}
            readOnly={snap.readOnly}
            onExportPreview={onExportPreview} onExportPreviewVideo={onExportPreviewVideo}
            preparedVideo={preparedVideo} setPreparedVideo={setPreparedVideo}
            videoBusy={videoBusy} setVideoBusy={setVideoBusy}
            videoPreset={videoPreset} setVideoPreset={setVideoPreset}
            layersPartKey={layersPartKey} uiLayers={uiLayers}
            variant="mobile"
          />
        </div>
      </div>

      {/* Next sections will be appended here */}

      <SkinToneSection snap={snap} skinOpen={skinOpen} setSkinOpen={setSkinOpen} variant="mobile" />
      <AppearanceSection
        snap={snap} opts={opts} hairIsNone={hairIsNone}
        appearanceOpen={appearanceOpen} setAppearanceOpen={setAppearanceOpen}
        hairMode={hairMode} hairPreset={hairPreset} hairCustom={hairCustom}
        topMode={topMode} topPreset={topPreset} topCustom={topCustom}
        bottomMode={bottomMode} bottomPreset={bottomPreset} bottomCustom={bottomCustom}
        setHair={setHair} setTop={setTop} setBottom={setBottom}
      />

      {/* Pose morphs (separate from body morphs) */}
      <div className="section">
        <div className="label">Pose</div>

        {snap.poseMorphNames?.length ? (
          <div
            className="tt-row"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(56px, 1fr))",
              gap: 8,
            }}
          >
            {snap.poseMorphNames.map((fullName) => {
              const label = String(fullName).replace(/^POSE__/, "");
              const w = (snap.poseWeights && snap.poseWeights[fullName]) || 0;
              const isOn = w > 0.5;

              const src = poseThumbSrc(label);

              return (
                <button
                  key={fullName}
                  type="button"
                  className={`tt-chip ${isOn ? "is-selected" : ""}`}
                  onClick={() => setPoseWeight(fullName, 1)}
                  title={`${label} (${Math.round(w * 100)}%)`}
                  aria-label={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: 8,
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={src}
                    alt=""
                    draggable={false}
                    style={{
                      width: 34,
                      height: 34,
                      opacity: isOn ? 1 : 0.85,
                    }}
                  />
                  {/* optional: show label under icon if you want */}
                  {/* <span style={{ fontSize: 12 }}>{label}</span> */}
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.35 }}>
            Pose morphs not detected yet. (They’ll appear after the model loads
            and calls <code>setPoseMorphNames()</code>.)
          </div>
        )}
      </div>

      {/* Templates */}
      <div className="section">
        <div className="label">
          Download Templates{" "}
          <button
            className="mini-toggle"
            onClick={() => setShowTemplates((s) => !s)}
          >
            {showTemplates ? "–" : "+"}
          </button>
        </div>
        {showTemplates && (
          <div className="tt-row" style={{ flexWrap: "wrap", gap: 8 }}>
            {Object.entries(snap.uvGuides || {}).map(([k, url]) =>
              url ? (
                <button
                  key={k}
                  type="button"
                  className="tt-chip"
                  onClick={() => downloadUVGuide(k)}
                >
                  {k.replace("torso", "Torso").replace("legs", "Legs")}
                </button>
              ) : null
            )}
            <button
              type="button"
              className="tt-chip"
              onClick={downloadAllUVGuides}
            >
              Download all
            </button>
          </div>
        )}
      </div>

      <FlashSection />

      <ArtistDirectorySection />

      {!snap.readOnly && hasSomethingToShare && (
        <LeadFormSection
          renderedImageUrls={renderedImageUrls}
          placementUrl={placementUrl}
          onRefreshPreview={onRefreshPreview}
          quoteOpen={quoteOpen}
          setQuoteOpen={setQuoteOpen}
        />
      )}

      {/* Clear all */}
      <div className="tt-row clear-all-btn" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="tt-btn tt-btn--fill-danger tt-grow"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (
              typeof window !== "undefined" &&
              window.confirm(
                "Clear all layers and settings for this design?\n\nThis can’t be undone."
              )
            ) {
              clearAll();
            }
          }}
        >
          Clear all Artwork
        </button>
      </div>

      <JoinWizardSection showJoin={showJoin} setShowJoin={setShowJoin} />
      <ArtistWizardSection
        showArtistWizard={showArtistWizard} setShowArtistWizard={setShowArtistWizard}
        shopProfile={shopProfile} onSubmitArtistProfile={onSubmitArtistProfile}
        showArtistUpgrade={showArtistUpgrade} setShowArtistUpgrade={setShowArtistUpgrade}
        artistTrialStarted={artistTrialStarted} artistTrialDaysLeft={artistTrialDaysLeft}
      />
    </>
  );
}
